import JSZip from 'jszip';

export const MAX_ATTACHMENT_BYTES = 12 * 1024 * 1024;
export const MAX_REQUEST_BYTES = 12 * 1024 * 1024;

const BLOCKED = /\.(msg|sql|gz|tgz|rar|jar|exe|dll|bin)$/i;
const MIN_PAGE_CHARS = 40;

export type AttachmentStatus =
  | 'referenced'
  | 'unreferenced'
  | 'duplicate'
  | 'too-large'
  | 'blocked';

export interface ExportAttachment {
  name: string;
  file: File;
  size: number;
  versions: number;
  author?: string;
  date?: string;
  status: AttachmentStatus;
  duplicateOf?: string;
}

export interface ExportPage {
  fileName: string;
  title: string;
  content: string;
  almostEmpty: boolean;
  sectionHome: boolean;
  attachments: ExportAttachment[];
}

export interface ExportReading {
  pages: ExportPage[];
  withoutPage: string[];
}

export const decodeName = (name: string): string => {
  const spaced = name.replace(/\+/g, ' ');
  try {
    return decodeURIComponent(spaced);
  } catch {
    return spaced;
  }
};

const norm = (name: string) =>
  decodeName(name).normalize('NFC').trim().toLowerCase();

const baseName = (target: string) => target.split('/').pop() ?? target;

export function referencedNames(content: string): Set<string> {
  const out = new Set<string>();
  const add = (target: string | undefined) => {
    const t = (target ?? '').trim().replace(/^attach:/i, '');
    if (t) out.add(norm(baseName(t)));
  };
  for (const m of content.matchAll(/\[([^[\]]+)\]/g)) {
    const parts = m[1].split('|');
    add(parts[parts.length - 1]);
  }
  for (const m of content.matchAll(
    /src\s*=\s*(?:'([^']+)'|"([^"]+)"|([^\s}'"]+))/g,
  )) {
    add(m[1] ?? m[2] ?? m[3]);
  }
  return out;
}

function parseProperties(text: string) {
  const author = new Map<number, string>();
  let date: string | undefined;
  for (const line of text.split(/\r?\n/)) {
    if (line.startsWith('#')) {
      if (!/JSPWiki page properties/i.test(line) && !date) {
        date = line.slice(1).trim();
      }
      continue;
    }
    const m = line.match(/^(\d+)\.author=(.*)$/);
    if (m) author.set(Number(m[1]), m[2].trim());
  }
  return { author, date };
}

async function sha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    await file.arrayBuffer(),
  );
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

interface RawAttachment {
  versions: Map<number, File>;
  props?: File;
}

export async function readExport(list: File[]): Promise<ExportReading> {
  const pageFiles = new Map<string, File>();
  const attDirs = new Map<string, Map<string, RawAttachment>>();

  for (const file of list) {
    const parts = (file.webkitRelativePath || file.name).split('/');
    const fname = parts[parts.length - 1];
    if (fname === '.DS_Store' || parts.includes('OLD')) continue;
    const dir = parts[parts.length - 2] ?? '';
    const att = parts[parts.length - 3] ?? '';
    if (dir.endsWith('-dir') && att.endsWith('-att')) {
      const pageKey = att.slice(0, -4);
      const perPage = attDirs.get(pageKey) ?? new Map<string, RawAttachment>();
      attDirs.set(pageKey, perPage);
      const raw = perPage.get(dir) ?? { versions: new Map<number, File>() };
      perPage.set(dir, raw);
      if (fname === 'attachment.properties') raw.props = file;
      else {
        const v = fname.match(/^(\d+)\./);
        if (v) raw.versions.set(Number(v[1]), file);
      }
      continue;
    }
    if (parts.some(p => p.endsWith('-att') || p.endsWith('-dir'))) continue;
    const m = fname.match(/^(.*)\.(txt|md)$/i);
    if (m && !pageFiles.has(m[1])) pageFiles.set(m[1], file);
  }

  const pages: ExportPage[] = [];
  for (const [key, file] of pageFiles) {
    const content = await file.text();
    const refs = referencedNames(content);
    const raws = attDirs.get(key) ?? new Map<string, RawAttachment>();
    const items: ExportAttachment[] = [];
    for (const [dir, raw] of raws) {
      if (raw.versions.size === 0) continue;
      const last = Math.max(...raw.versions.keys());
      const latest = raw.versions.get(last)!;
      const name = decodeName(dir.slice(0, -4));
      const props = raw.props
        ? parseProperties(await raw.props.text())
        : undefined;
      const referenced = refs.has(norm(name));
      let status: AttachmentStatus = referenced ? 'referenced' : 'unreferenced';
      if (BLOCKED.test(name)) status = 'blocked';
      else if (latest.size > MAX_ATTACHMENT_BYTES) status = 'too-large';
      items.push({
        name,
        file: latest,
        size: latest.size,
        versions: raw.versions.size,
        author: props?.author.get(last),
        date: props?.date,
        status,
      });
    }
    items.sort((a, b) => {
      const ra = a.status === 'referenced' ? 0 : 1;
      const rb = b.status === 'referenced' ? 0 : 1;
      return ra - rb || a.name.localeCompare(b.name);
    });
    const seen = new Map<string, string>();
    for (const a of items) {
      if (a.status === 'blocked' || a.status === 'too-large') continue;
      const hash = await sha256(a.file);
      const first = seen.get(hash);
      if (first && a.status === 'unreferenced') {
        a.status = 'duplicate';
        a.duplicateOf = first;
      } else if (!first) {
        seen.set(hash, a.name);
      }
    }
    pages.push({
      fileName: file.name,
      title: decodeName(key),
      content,
      almostEmpty: content.trim().length < MIN_PAGE_CHARS,
      sectionHome: /^Main\w*Section\.(txt|md)$/i.test(file.name),
      attachments: items,
    });
  }
  pages.sort((a, b) => a.title.localeCompare(b.title));
  const withoutPage = [...attDirs.keys()]
    .filter(k => !pageFiles.has(k))
    .map(decodeName);
  return { pages, withoutPage };
}

export const attachmentSelectedByDefault = (a: ExportAttachment) =>
  a.status === 'referenced';

export const attachmentSelectable = (a: ExportAttachment) =>
  a.status === 'referenced' || a.status === 'unreferenced';

export function splitInBatches(
  items: ExportAttachment[],
): ExportAttachment[][] {
  const batches: ExportAttachment[][] = [];
  let current: ExportAttachment[] = [];
  let bytes = 0;
  for (const a of items) {
    if (current.length > 0 && bytes + a.size > MAX_REQUEST_BYTES) {
      batches.push(current);
      current = [];
      bytes = 0;
    }
    current.push(a);
    bytes += a.size;
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

export async function toBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export async function filesFromZip(zip: File): Promise<File[]> {
  const archive = await JSZip.loadAsync(await zip.arrayBuffer());
  const out: File[] = [];
  for (const entry of Object.values(archive.files)) {
    if (entry.dir || entry.name.startsWith('__MACOSX/')) continue;
    const file = new File(
      [await entry.async('blob')],
      entry.name.split('/').pop() ?? entry.name,
    );
    Object.defineProperty(file, 'webkitRelativePath', { value: entry.name });
    out.push(file);
  }
  return out;
}

export async function expandZips(list: File[]): Promise<File[]> {
  const out: File[] = [];
  for (const f of list) {
    if (/\.zip$/i.test(f.name)) out.push(...(await filesFromZip(f)));
    else out.push(f);
  }
  return out;
}
